// Лента чата строится из событий треда. Та же функция работает и при первой загрузке
// (события из базы), и при живом потоке, и после перезагрузки страницы.
import type { Attachment } from './attachments.js';
import type { CliAssistant, CliCommandLifecycle, CliContentBlock, CliResult, CliUser } from './cli.js';

/** Событие треда в том виде, в каком его отдаёт api (из agent_events). */
export interface ThreadEvent {
  id: number;
  runId: string;
  seq: number;
  ts: string;
  type: string;
  subtype?: string | null;
  payload: Record<string, unknown>;
}

// Служебные события платформы (кладутся в agent_events рядом с событиями CLI)
export interface UserMessagePayload {
  uuid: string;
  text: string;
  priority?: 'now' | 'next' | 'later';
  /** Вложения владельца (картинки, видео, файлы) — лента показывает их миниатюрами и карточками. */
  attachments?: Attachment[];
  /** Задание фоновой работы (брифинг, повод, продолжение) — пишет платформа, а не владелец. */
  background?: boolean;
}
export interface PermissionRequestPayload {
  requestId: string;
  toolName: string;
  toolUseId?: string;
  input: Record<string, unknown>;
  expiresAt: string;
}
export interface PermissionDecisionPayload {
  requestId: string;
  decision: 'allow' | 'deny' | 'expired';
  message?: string;
}
/** Владелец сменил модель или глубину размышлений чата (вступило в силу). */
export interface ThreadSettingsPayload {
  model: string;
  effort: string | null;
}
export interface RunLifecyclePayload {
  model?: string;
  resume?: boolean;
  code?: number | null;
  signal?: string | null;
  reason?: string;
}

export type ToolStatus = 'running' | 'done' | 'error' | 'denied' | 'interrupted';

export type Block =
  | { kind: 'text'; key: string; text: string; streaming?: boolean }
  | {
      kind: 'tool';
      key: string;
      toolUseId: string;
      name: string;
      input: Record<string, unknown>;
      status: ToolStatus;
      output?: string;
    }
  | {
      kind: 'permission';
      key: string;
      requestId: string;
      toolName: string;
      input: Record<string, unknown>;
      status: 'pending' | 'allow' | 'deny' | 'expired';
      expiresAt: string;
    }
  | { kind: 'notice'; key: string; level: 'info' | 'warning' | 'error'; text: string };

export interface UserTurn {
  kind: 'user';
  key: string;
  uuid: string;
  runId: string;
  text: string;
  ts: string;
  state: 'sending' | 'queued' | 'started' | 'completed' | 'cancelled';
  attachments?: Attachment[];
  /** Задание фоновой работы от платформы (не слова владельца): лента показывает его свёрнутым. */
  background?: boolean;
}

export interface AgentTurn {
  kind: 'agent';
  key: string;
  runId: string;
  model?: string;
  ts: string;
  blocks: Block[];
  status: 'working' | 'done' | 'stopped' | 'error' | 'interrupted';
  durationMs?: number;
  costUsd?: number;
  errorText?: string;
}

/** Отметка в ленте: с этого места агент работает на другой модели. */
export interface SystemTurn {
  kind: 'system';
  key: string;
  ts: string;
  model: string;
  effort: string | null;
}

export type Turn = UserTurn | AgentTurn | SystemTurn;

export interface ThreadView {
  turns: Turn[];
  /** Сообщения, которые агент ещё не прочитал (в очереди CLI или отправляются). */
  queued: UserTurn[];
  /** Идёт ли сейчас ход агента. */
  working: boolean;
  /** Ждёт ли агент решения владельца. */
  waiting: boolean;
  lastEventId: number;
  /** Сколько стоит тред по данным CLI (накопительно по запускам). */
  costUsd: number;
  /** Живой частичный текст текущего блока (не из базы). */
  partial: { runId: string; index: number; text: string } | null;
  thinking: boolean;
  /** Последний накопительный total_cost_usd по запуску — для вычисления стоимости хода. */
  runCost: Record<string, number>;
  /** Последняя осмысленная ошибка процесса агента (из stderr) по запуску. */
  runErrors: Record<string, string>;
}

export function emptyThread(): ThreadView {
  return {
    turns: [],
    queued: [],
    working: false,
    waiting: false,
    lastEventId: 0,
    costUsd: 0,
    partial: null,
    thinking: false,
    runCost: {},
    runErrors: {},
  };
}

function toolOutput(block: Extract<CliContentBlock, { type: 'tool_result' }>): string {
  const c = block.content;
  const s = typeof c === 'string' ? c : Array.isArray(c) ? c.map((x) => x.text ?? '').join('\n') : '';
  return s.length > 4000 ? `${s.slice(0, 4000)}\n…` : s;
}

function currentAgent(v: ThreadView, ev: ThreadEvent, model?: string): AgentTurn {
  const last = v.turns[v.turns.length - 1];
  if (last && last.kind === 'agent' && last.status === 'working' && last.runId === ev.runId) return last;
  const turn: AgentTurn = {
    kind: 'agent',
    key: `a${ev.id}`,
    runId: ev.runId,
    ts: ev.ts,
    blocks: [],
    status: 'working',
    ...(model ? { model } : {}),
  };
  v.turns.push(turn);
  return turn;
}

function findTool(v: ThreadView, toolUseId: string): Extract<Block, { kind: 'tool' }> | undefined {
  for (let i = v.turns.length - 1; i >= 0; i--) {
    const t = v.turns[i];
    if (t?.kind !== 'agent') continue;
    const b = t.blocks.find((x) => x.kind === 'tool' && x.toolUseId === toolUseId);
    if (b && b.kind === 'tool') return b;
  }
  return undefined;
}

function findPermission(
  v: ThreadView,
  requestId: string,
): Extract<Block, { kind: 'permission' }> | undefined {
  for (let i = v.turns.length - 1; i >= 0; i--) {
    const t = v.turns[i];
    if (t?.kind !== 'agent') continue;
    const b = t.blocks.find((x) => x.kind === 'permission' && x.requestId === requestId);
    if (b && b.kind === 'permission') return b;
  }
  return undefined;
}

function startQueued(v: ThreadView, uuid: string, state: UserTurn['state']): void {
  const i = v.queued.findIndex((q) => q.uuid === uuid);
  if (i < 0) {
    const t = v.turns.find((x): x is UserTurn => x.kind === 'user' && x.uuid === uuid);
    // уже прочитанное сообщение не «отменяется»: CLI шлёт cancelled для хода, оборванного priority:"now"
    if (t && !(state === 'cancelled' && t.state !== 'queued')) t.state = state;
    return;
  }
  const [q] = v.queued.splice(i, 1);
  if (!q) return;
  q.state = state;
  if (state === 'cancelled') return;
  // Сообщение попало в разговор именно сейчас: закрываем текущий сегмент ответа агента
  v.turns.push(q);
}

function finishWorking(v: ThreadView, status: AgentTurn['status'], runId?: string): void {
  for (const t of v.turns) {
    if (t.kind !== 'agent' || t.status !== 'working') continue;
    if (runId && t.runId !== runId) continue;
    t.status = status;
    for (const b of t.blocks) {
      if (b.kind === 'tool' && b.status === 'running') b.status = status === 'done' ? 'done' : 'interrupted';
    }
  }
}

/** Применить одно событие к виду треда (мутирует и возвращает тот же объект). */
export function applyEvent(v: ThreadView, ev: ThreadEvent): ThreadView {
  if (ev.id <= v.lastEventId) return v;
  v.lastEventId = ev.id;
  const p = ev.payload;

  switch (ev.type) {
    case 'user_message': {
      const m = p as unknown as UserMessagePayload;
      if (
        v.queued.some((q) => q.uuid === m.uuid) ||
        v.turns.some((t) => t.kind === 'user' && t.uuid === m.uuid)
      )
        break;
      v.queued.push({
        kind: 'user',
        key: `u${m.uuid}`,
        uuid: m.uuid,
        runId: ev.runId,
        text: m.text,
        ts: ev.ts,
        state: 'queued',
        ...(Array.isArray(m.attachments) && m.attachments.length ? { attachments: m.attachments } : {}),
        ...(m.background === true ? { background: true } : {}),
      });
      v.working = true;
      break;
    }
    case 'command_lifecycle': {
      const c = p as unknown as CliCommandLifecycle;
      if (c.state === 'started' || c.state === 'cancelled' || c.state === 'completed')
        startQueued(v, c.command_uuid, c.state);
      break;
    }
    case 'user': {
      const u = p as unknown as CliUser;
      if (u.isReplay) {
        const uuid = typeof u.uuid === 'string' ? u.uuid : '';
        if (uuid) startQueued(v, uuid, 'started');
        break;
      }
      if (u.parent_tool_use_id) break;
      const content = u.message?.content;
      if (!Array.isArray(content)) break;
      for (const b of content) {
        if (b.type !== 'tool_result') continue;
        const tool = findTool(v, b.tool_use_id);
        if (!tool) continue;
        tool.output = toolOutput(b);
        if (b.is_error) {
          const denied = /permission|denied|doesn't want to proceed|not allowed/i.test(tool.output ?? '');
          tool.status = denied ? 'denied' : 'error';
        } else tool.status = 'done';
      }
      break;
    }
    case 'assistant': {
      const a = p as unknown as CliAssistant;
      if (a.parent_tool_use_id) break; // шаги субагентов в ленте не показываем
      const model = a.message?.model && !a.message.model.startsWith('<') ? a.message.model : undefined;
      const turn = currentAgent(v, ev, model);
      if (a.error) {
        const raw = a.message?.content?.map((c) => (c.type === 'text' ? c.text : '')).join('') ?? a.error;
        const text = humanApiError(a.error, raw);
        turn.blocks.push({ kind: 'notice', key: `n${ev.id}`, level: 'error', text });
        turn.errorText = text;
        break;
      }
      for (const [i, c] of (a.message?.content ?? []).entries()) {
        if (c.type === 'text' && c.text) {
          turn.blocks.push({ kind: 'text', key: `t${ev.id}_${i}`, text: c.text });
          if (v.partial?.runId === ev.runId) v.partial = null;
        } else if (c.type === 'tool_use') {
          turn.blocks.push({
            kind: 'tool',
            key: `k${c.id}`,
            toolUseId: c.id,
            name: c.name,
            input: c.input ?? {},
            status: 'running',
          });
        }
      }
      v.thinking = false;
      break;
    }
    case 'result': {
      const r = p as unknown as CliResult;
      const turn = v.turns[v.turns.length - 1];
      const prev = v.runCost[ev.runId] ?? 0;
      const total = typeof r.total_cost_usd === 'number' ? r.total_cost_usd : prev;
      const turnCost = Math.max(0, total - prev);
      v.runCost[ev.runId] = total;
      v.costUsd += turnCost;
      const status: AgentTurn['status'] =
        r.terminal_reason === 'aborted_tools' || r.terminal_reason === 'aborted_streaming'
          ? 'stopped'
          : r.is_error
            ? 'error'
            : 'done';
      if (turn && turn.kind === 'agent' && turn.status === 'working') {
        if (typeof r.duration_ms === 'number') turn.durationMs = r.duration_ms;
        turn.costUsd = turnCost;
        if (status === 'error' && !turn.errorText && r.result) {
          turn.errorText = r.result;
          turn.blocks.push({ kind: 'notice', key: `n${ev.id}`, level: 'error', text: r.result });
        }
      }
      finishWorking(v, status, ev.runId);
      v.partial = null;
      v.thinking = false;
      v.working = v.queued.length > 0;
      break;
    }
    case 'thread_settings': {
      const s = p as unknown as ThreadSettingsPayload;
      const last = v.turns[v.turns.length - 1];
      // Несколько смен подряд без разговора между ними — показываем только итоговую
      if (last?.kind === 'system') v.turns.pop();
      v.turns.push({ kind: 'system', key: `s${ev.id}`, ts: ev.ts, model: s.model, effort: s.effort ?? null });
      break;
    }
    case 'permission_request': {
      const r = p as unknown as PermissionRequestPayload;
      const turn = currentAgent(v, ev);
      turn.blocks.push({
        kind: 'permission',
        key: `p${r.requestId}`,
        requestId: r.requestId,
        toolName: r.toolName,
        input: r.input,
        status: 'pending',
        expiresAt: r.expiresAt,
      });
      v.waiting = true;
      break;
    }
    case 'permission_decision': {
      const d = p as unknown as PermissionDecisionPayload;
      const b = findPermission(v, d.requestId);
      if (b) b.status = d.decision;
      v.waiting = v.turns.some(
        (t) => t.kind === 'agent' && t.blocks.some((x) => x.kind === 'permission' && x.status === 'pending'),
      );
      break;
    }
    case 'runner': {
      if (ev.subtype === 'stderr') {
        const line = String(p.text ?? '');
        if (/^(error|fatal)\b|Error:/i.test(line) && !/^\s*at\s/.test(line)) v.runErrors[ev.runId] = line;
      }
      if (ev.subtype === 'exit') {
        const x = p as unknown as RunLifecyclePayload;
        // Сообщения этого запуска, которые агент так и не прочитал: показываем их и объясняем, что случилось
        const lost = v.queued.filter((q) => q.runId === ev.runId);
        if (lost.length && x.reason !== 'idle') {
          v.queued = v.queued.filter((q) => q.runId !== ev.runId);
          for (const q of lost) v.turns.push({ ...q, state: 'cancelled' });
          const err = v.runErrors[ev.runId];
          v.turns.push({
            kind: 'agent',
            key: `x${ev.id}`,
            runId: ev.runId,
            ts: ev.ts,
            status: 'error',
            errorText: err ?? 'crash',
            blocks: [
              {
                kind: 'notice',
                key: `xn${ev.id}`,
                level: 'error',
                text: `${humanRunError(err)} Напишите сообщение ещё раз — агент запустится заново.`,
              },
            ],
          });
        }
        const hadWork = v.turns.some(
          (t) => t.kind === 'agent' && t.status === 'working' && t.runId === ev.runId,
        );
        finishWorking(v, 'interrupted', ev.runId);
        if (hadWork && x.reason !== 'idle') {
          const last = v.turns[v.turns.length - 1];
          if (last?.kind === 'agent') {
            last.blocks.push({
              kind: 'notice',
              key: `x${ev.id}`,
              level: 'warning',
              text: 'Работа прервалась. Напишите сообщение — агент продолжит с того же места.',
            });
          }
        }
        v.partial = v.partial?.runId === ev.runId ? null : v.partial;
        v.working = v.queued.length > 0;
        v.thinking = false;
        v.waiting = false;
        for (const t of v.turns) {
          if (t.kind !== 'agent') continue;
          for (const b of t.blocks)
            if (b.kind === 'permission' && b.status === 'pending') b.status = 'expired';
        }
      }
      break;
    }
    default:
      break;
  }
  return v;
}

export function buildThread(events: ThreadEvent[]): ThreadView {
  const v = emptyThread();
  for (const e of events) applyEvent(v, e);
  return v;
}

/** Живая дельта текста (не хранится в базе). */
export function applyDelta(v: ThreadView, runId: string, index: number, text: string): ThreadView {
  if (v.partial && v.partial.runId === runId && v.partial.index === index) v.partial.text += text;
  else v.partial = { runId, index, text };
  v.thinking = false;
  v.working = true;
  return v;
}

/** Как назвать шаг: сделанный, идущий сейчас или запрос разрешения на него. */
export type ToolTense = 'done' | 'running' | 'request';

const TOOL_WORDS: Record<string, [string, string, string]> = {
  Read: ['Прочитал файл', 'Читает файл', 'Прочитать файл'],
  Write: ['Записал файл', 'Записывает файл', 'Записать файл'],
  Edit: ['Изменил файл', 'Меняет файл', 'Изменить файл'],
  MultiEdit: ['Изменил файл', 'Меняет файл', 'Изменить файл'],
  Grep: ['Искал в файлах', 'Ищет в файлах', 'Искать в файлах'],
  Glob: ['Искал файлы', 'Ищет файлы', 'Найти файлы'],
  Bash: ['Выполнил команду', 'Выполняет команду', 'Выполнить команду'],
  WebSearch: ['Искал в интернете', 'Ищет в интернете', 'Искать в интернете'],
  WebFetch: ['Открыл страницу', 'Открывает страницу', 'Открыть страницу'],
  Task: ['Поручил субагенту', 'Работает субагент', 'Поручить субагенту'],
  Agent: ['Поручил субагенту', 'Работает субагент', 'Поручить субагенту'],
  TodoWrite: ['Обновил план работы', 'Обновляет план работы', 'Обновить план работы'],
};

/** Короткое человеческое описание шага агента для карточки. */
export function describeTool(
  name: string,
  input: Record<string, unknown>,
  tense: ToolTense = 'done',
): { title: string; detail?: string } {
  const s = (k: string) => (typeof input[k] === 'string' ? (input[k] as string) : undefined);
  const file = (p?: string) => (p ? p.split(/[\\/]/).slice(-2).join('/') : undefined);
  const i = tense === 'done' ? 0 : tense === 'running' ? 1 : 2;
  const words = TOOL_WORDS[name];
  const title =
    words?.[i] ??
    (name.startsWith('mcp__platform__') ? name.slice('mcp__platform__'.length).replace(/_/g, ' ') : name);
  switch (name) {
    case 'Read':
    case 'Write':
    case 'Edit':
    case 'MultiEdit':
      return { title, detail: file(s('file_path')) };
    case 'Grep':
    case 'Glob':
      return { title, detail: s('pattern') };
    case 'Bash':
      // Описание от модели понятнее команды; сама команда — в подробностях
      return {
        title: tense === 'done' && s('description') ? (s('description') as string) : title,
        detail: s('command'),
      };
    case 'WebSearch':
      return { title, detail: s('query') };
    case 'WebFetch':
      return { title, detail: s('url') };
    case 'Task':
    case 'Agent':
      return { title, detail: s('description') };
    default:
      return { title };
  }
}

/** Понятный текст для ошибок API Claude, которые CLI отдаёт в assistant.error. */
export function humanApiError(code: string, raw: string): string {
  switch (code) {
    case 'authentication_failed':
    case 'oauth_org_not_allowed':
      return 'Нет доступа к подписке Claude: нужен действующий токен (claude setup-token).';
    case 'rate_limit':
      return `Лимит подписки исчерпан. ${raw.replace(/^You've hit your /, 'Достигнут ')}`.trim();
    case 'billing_error':
    case 'account_on_hold':
      return 'Подписка Claude недоступна — проверьте оплату в аккаунте Claude.';
    case 'overloaded':
    case 'server_error':
      return 'Серверы Claude сейчас перегружены. Попробуйте через минуту.';
    case 'model_not_found':
      return 'Выбранная модель недоступна на этой подписке.';
    default:
      return raw || 'Claude вернул ошибку.';
  }
}

/** Понятный текст для падения процесса агента по последней строке stderr. */
export function humanRunError(line: string | undefined): string {
  if (!line) return 'Агент неожиданно остановился.';
  if (/bubblewrap/i.test(line)) return 'Агент не запустился: в контейнере не хватает песочницы (bubblewrap).';
  if (/not logged in|authentication|oauth/i.test(line))
    return 'Агент не запустился: нет доступа к подписке Claude.';
  if (/ENOENT|spawn/i.test(line)) return 'Агент не запустился: не найден Claude CLI.';
  return `Агент не запустился: ${line.replace(/^error:\s*/i, '').slice(0, 200)}`;
}
